import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { api } from "@/lib/api";

const BrandingContext = createContext(null);

const DEFAULT_BRANDING = {
  company_name: "Tragedys Mad House ENT",
  tagline: "Online Command Center",
  logo_url: null,
  accent_color: "#ff0000",
  accent_hover: "#c8001b",
  login_hero_url:
    "https://images.unsplash.com/photo-1758520145408-dedb359d1c49?crop=entropy&cs=srgb&fm=jpg&h=1200&w=1600&q=80",
};

export function BrandingProvider({ children }) {
  const [brand, setBrand] = useState(DEFAULT_BRANDING);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/branding");
      const merged = { ...DEFAULT_BRANDING, ...data };
      setBrand(merged);
      applyCssVars(merged);
      updateFavicon(merged);
      document.title = `${merged.company_name} · ${merged.tagline}`;
    } catch (err) {
      console.warn("Branding load failed, using defaults:", err?.message || err);
      applyCssVars(DEFAULT_BRANDING);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = useCallback(async (payload) => {
    const { data } = await api.put("/branding", payload);
    const merged = { ...DEFAULT_BRANDING, ...data };
    setBrand(merged);
    applyCssVars(merged);
    updateFavicon(merged);
    document.title = `${merged.company_name} · ${merged.tagline}`;
    return merged;
  }, []);

  const value = useMemo(() => ({ brand, refresh: load, save }), [brand, load, save]);

  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
}

function applyCssVars(b) {
  const root = document.documentElement;
  root.style.setProperty("--accent", b.accent_color);
  root.style.setProperty("--accent-hover", b.accent_hover);
}

function updateFavicon(b) {
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'><rect width='100' height='100' rx='22' fill='${encodeURIComponent(
    b.accent_color,
  )}'/><text x='50%' y='58%' font-size='60' text-anchor='middle' font-family='Outfit, sans-serif' font-weight='800' fill='%230b0d12'>${(b.company_name || "N")[0].toUpperCase()}</text></svg>`;
  const url = `data:image/svg+xml;utf8,${svg}`;
  let link = document.querySelector("link[rel='icon']");
  if (!link) {
    link = document.createElement("link");
    link.rel = "icon";
    document.head.appendChild(link);
  }
  link.href = url;
}

export const useBranding = () => useContext(BrandingContext);
